import api, { API_URL } from '../services/api';

export const JOB_SAFETY_CONTENT_SLUG = 'how-to-tell-stagehand-job-listing-is-legit';
export const PAY_TRANSPARENCY_CONTENT_SLUG = 'stagecrew-pay-transparency-report-2026';

export type ContentSource = 'stagecrew_blog' | 'medium';
export type ContentDestination = 'jobs' | 'registration';

export interface ContentAttribution {
  source: ContentSource;
  contentSlug: string;
}

const CONTENT_ATTRIBUTION_KEY = 'stagecrew:content-attribution';
const CONTENT_DESTINATION_KEY = 'stagecrew:content-destination';
const TRACKED_CONTENT_SLUGS = new Set([
  JOB_SAFETY_CONTENT_SLUG,
  PAY_TRANSPARENCY_CONTENT_SLUG,
]);
const PENDING_EVENT_TIMEOUT_MS = 30_000;

function readSessionValue<T>(key: string): T | null {
  try {
    const value = window.sessionStorage.getItem(key);
    return value ? JSON.parse(value) as T : null;
  } catch {
    return null;
  }
}

function writeSessionValue(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Content measurement must not affect the product experience.
  }
}

function removeSessionValue(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Content measurement must not affect the product experience.
  }
}

function isContentAttribution(value: unknown): value is ContentAttribution {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<ContentAttribution>;
  return (
    TRACKED_CONTENT_SLUGS.has(candidate.contentSlug || '')
    && (candidate.source === 'stagecrew_blog' || candidate.source === 'medium')
  );
}

function eventKey(eventName: string, attribution: ContentAttribution): string {
  return `stagecrew:content-event:${eventName}:${attribution.source}:${attribution.contentSlug}`;
}

interface ContentEventState {
  state: 'pending' | 'recorded';
  startedAt?: number;
}

function readContentEventState(key: string): ContentEventState | null {
  const value = readSessionValue<unknown>(key);
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<ContentEventState>;
  if (candidate.state === 'recorded') return { state: 'recorded' };
  if (candidate.state === 'pending' && typeof candidate.startedAt === 'number') {
    return { state: 'pending', startedAt: candidate.startedAt };
  }
  return null;
}

async function recordAggregateContentEvent(
  eventName: 'article_view' | 'article_jobs_click' | 'article_registration_click',
  attribution: ContentAttribution,
): Promise<boolean> {
  const key = eventKey(eventName, attribution);
  const existing = readContentEventState(key);
  if (existing?.state === 'recorded') return true;
  if (
    existing?.state === 'pending'
    && existing.startedAt
    && (Date.now() - existing.startedAt) < PENDING_EVENT_TIMEOUT_MS
  ) {
    return false;
  }

  const pendingState: ContentEventState = {
    state: 'pending',
    startedAt: Date.now(),
  };
  writeSessionValue(key, pendingState);

  try {
    const response = await fetch(`${API_URL}/api/product-events/content`, {
      method: 'POST',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        eventName,
        source: attribution.source,
        contentSlug: attribution.contentSlug,
      }),
    });
    if (!response.ok) {
      throw new Error('Content event was not accepted');
    }
    writeSessionValue(key, { state: 'recorded' } satisfies ContentEventState);
    return true;
  } catch {
    const current = readContentEventState(key);
    if (
      current?.state === 'pending'
      && current.startedAt === pendingState.startedAt
    ) {
      removeSessionValue(key);
    }
    return false;
  }
}

export function getContentAttribution(): ContentAttribution | null {
  const value = readSessionValue<unknown>(CONTENT_ATTRIBUTION_KEY);
  return isContentAttribution(value) ? value : null;
}

export function clearContentAttribution(): void {
  removeSessionValue(CONTENT_ATTRIBUTION_KEY);
  removeSessionValue(CONTENT_DESTINATION_KEY);
}

export function recordArticleView(contentSlug: string, search: string): void {
  if (!TRACKED_CONTENT_SLUGS.has(contentSlug)) return;
  const searchParams = new URLSearchParams(search);
  // Rendered production QA should not inflate the acquisition baseline.
  if (searchParams.get('stagecrew_qa') === '1') return;
  const querySource = searchParams.get('utm_source');
  const current = getContentAttribution();
  const attribution: ContentAttribution = {
    source: querySource === 'medium'
      ? 'medium'
      : (current?.contentSlug === contentSlug ? current.source : 'stagecrew_blog'),
    contentSlug,
  };
  writeSessionValue(CONTENT_ATTRIBUTION_KEY, attribution);
  void recordAggregateContentEvent('article_view', attribution);
}

export function markContentDestination(destination: ContentDestination): void {
  const attribution = getContentAttribution();
  if (!attribution) return;
  writeSessionValue(CONTENT_DESTINATION_KEY, { destination, ...attribution });
}

export function recordPendingContentDestination(destination: ContentDestination): void {
  const pending = readSessionValue<unknown>(CONTENT_DESTINATION_KEY);
  if (!pending || typeof pending !== 'object') return;
  const candidate = pending as Partial<ContentAttribution> & {
    destination?: ContentDestination;
  };
  if (candidate.destination !== destination || !isContentAttribution(candidate)) return;

  void recordAggregateContentEvent(
    destination === 'jobs' ? 'article_jobs_click' : 'article_registration_click',
    candidate,
  ).then((recorded) => {
    if (!recorded) return;
    const current = readSessionValue<unknown>(CONTENT_DESTINATION_KEY);
    if (!current || typeof current !== 'object') return;
    const currentCandidate = current as Partial<ContentAttribution> & {
      destination?: ContentDestination;
    };
    if (
      currentCandidate.destination === destination
      && currentCandidate.source === candidate.source
      && currentCandidate.contentSlug === candidate.contentSlug
    ) {
      removeSessionValue(CONTENT_DESTINATION_KEY);
    }
  });
}

/**
 * Record one authenticated visit per UTC day. The server derives the user and
 * timestamp; no path, search term, IP address, user agent, or metadata is sent.
 */
export function recordAuthenticatedDailyVisit(): void {
  const utcDay = new Date().toISOString().slice(0, 10);
  void Promise.resolve(api.post('/product-events', {
    eventName: 'site_visit',
    source: 'site',
    idempotencyKey: `site-visit:${utcDay}`,
  })).catch(() => {
    // Measurement must never interrupt the product experience.
  });
}
